import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import FaqAccordion, { FaqItem } from '../components/faq/FaqAccordion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

const Faq: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // FAQ data with categories
  const faqs: FaqItem[] = [
    {
      question: "Quels types de toitures proposez-vous ?",
      answer: "Chez MGE COUVERTURE, nous proposons une gamme complète de solutions de toiture adaptées à tous les types de bâtiments : tuiles (terre cuite, béton, ardoise), zinc, bac acier, membrane d'étanchéité, toiture terrasse, etc. Nous vous conseillons sur le matériau le plus adapté à votre projet en fonction de l'architecture de votre bâtiment, des contraintes locales et de votre budget.",
      category: "couverture"
    },
    {
      question: "Quelle est la durée de vie d'une toiture ?",
      answer: "La durée de vie d'une toiture dépend du matériau utilisé : les tuiles en terre cuite peuvent durer 50 à 100 ans, l'ardoise naturelle 80 à 100 ans, le zinc 50 à 100 ans, le bac acier 30 à 50 ans, et les membranes d'étanchéité 20 à 30 ans. Un entretien régulier permet d'optimiser cette durée de vie. Nous vous proposons des contrats de maintenance pour préserver votre investissement sur le long terme.",
      category: "couverture"
    },
    {
      question: "Quelles garanties offrez-vous sur vos travaux de toiture ?",
      answer: "Nous offrons toutes les garanties légales : la garantie de parfait achèvement (1 an), la garantie biennale (2 ans) et la garantie décennale (10 ans). Nous sommes assurés auprès de compagnies reconnues et fournissons les attestations d'assurance avec chaque devis. Pour certains matériaux, nous proposons également des garanties fabricant allant jusqu'à 30 ans. Notre engagement qualité se traduit par un suivi post-chantier rigoureux.",
      category: "garanties"
    },
    {
      question: "Comment se déroule un chantier de réfection de toiture ?",
      answer: "Un chantier de réfection de toiture se déroule en plusieurs étapes : diagnostic complet, établissement du devis, planification des travaux, installation des protections et échafaudages, dépose de l'ancienne couverture si nécessaire, réparation de la charpente si besoin, pose de la nouvelle couverture, et enfin nettoyage complet du chantier. Nous vous fournissons un planning détaillé avant le début des travaux et vous tenons informé quotidiennement de l'avancement.",
      category: "chantier"
    },
    {
      question: "Proposez-vous des solutions d'étanchéité pour toitures terrasses ?",
      answer: "Oui, nous sommes spécialisés dans l'étanchéité des toitures terrasses. Nous proposons différentes solutions : membranes bitumineuses, membranes synthétiques (PVC, EPDM), résines liquides, etc. Nous réalisons également des toitures végétalisées et des terrasses accessibles avec revêtement décoratif. Chaque solution est adaptée à l'usage de votre terrasse (accessible ou non) et aux contraintes techniques de votre bâtiment.",
      category: "étanchéité"
    },
    {
      question: "Comment détectez-vous une fuite sur une toiture ?",
      answer: "Nous utilisons plusieurs méthodes pour détecter les fuites : inspection visuelle approfondie, caméra thermique pour repérer les infiltrations d'eau, test d'arrosage ciblé, et dans certains cas, détection électronique pour les toitures terrasses. Notre expertise nous permet d'identifier rapidement l'origine des fuites, même les plus difficiles à localiser. Nous documentons nos recherches par des photos pour vous expliquer clairement la source du problème.",
      category: "diagnostic"
    },
    {
      question: "Fournissez-vous des rapports photographiques de vos travaux ?",
      answer: "Oui, nous documentons systématiquement toutes les étapes de nos chantiers par des photos. Vous recevez un rapport photographique complet montrant l'état initial, les différentes phases de travaux et le résultat final. Ces photos servent de référence et de preuve de la qualité de notre travail. Pour les zones inaccessibles (combles, points hauts), nous utilisons des drones pour capturer des images détaillées de l'état de votre toiture.",
      category: "documentation"
    },
    {
      question: "Pouvez-vous intervenir en urgence en cas de fuite ou de dégât sur ma toiture ?",
      answer: "Oui, nous disposons d'une équipe dédiée aux interventions d'urgence pour les fuites, dégâts suite à une tempête, ou autres problèmes nécessitant une action rapide. Nous intervenons dans les 24 à 48 heures selon la gravité de la situation. Notre service d'urgence comprend la mise en place de solutions temporaires (bâchage, etc.) pour protéger votre habitation en attendant les réparations définitives.",
      category: "urgence"
    },
    {
      question: "Quelles sont les options d'isolation pour ma toiture ?",
      answer: "Nous proposons plusieurs solutions d'isolation de toiture : isolation par l'extérieur (sarking), isolation des combles perdus (laine minérale, ouate de cellulose), isolation des combles aménagés (entre et sous chevrons), etc. Chaque solution offre différents avantages en termes de performance thermique, acoustique et de préservation de l'espace habitable. Nous vous conseillons la solution la plus adaptée à votre bâtiment et à vos besoins énergétiques.",
      category: "isolation"
    },
    {
      question: "Installez-vous des systèmes de récupération d'eau de pluie ?",
      answer: "Oui, nous installons des systèmes complets de récupération d'eau de pluie : gouttières, descentes, filtration, cuve de stockage et système de pompage. L'eau récupérée peut être utilisée pour l'arrosage du jardin, le lavage des véhicules, les toilettes ou même le lave-linge (avec traitement approprié). Cette solution écologique permet de réduire votre consommation d'eau potable et de réaliser des économies significatives.",
      category: "écologie"
    },
    {
      question: "Comment estimez-vous le coût d'une réfection de toiture ?",
      answer: "Nous établissons un devis détaillé après une visite technique approfondie de votre toiture. L'estimation prend en compte la surface, le type de matériaux choisis, la complexité de la toiture (pente, accessibilité, éléments particuliers), les travaux annexes nécessaires (charpente, isolation, zinguerie) et la main-d'œuvre. Nous vous expliquons chaque poste de dépense et vous proposons différentes options pour adapter le projet à votre budget.",
      category: "devis"
    },
    {
      question: "Proposez-vous l'installation de fenêtres de toit ou de puits de lumière ?",
      answer: "Oui, nous installons tous types de fenêtres de toit (Velux, Roto, etc.), verrières, puits de lumière et dômes. Ces éléments améliorent la luminosité naturelle et la ventilation de vos espaces sous toiture. Nous vous conseillons sur le positionnement optimal pour maximiser l'apport de lumière tout en préservant l'isolation thermique. Nous assurons également l'étanchéité parfaite autour de ces ouvertures pour éviter tout risque d'infiltration.",
      category: "aménagement"
    },
    {
      question: "Quelles sont les aides financières disponibles pour refaire ma toiture ?",
      answer: "Plusieurs aides financières sont disponibles pour les travaux de toiture, notamment lorsqu'ils incluent une amélioration de l'isolation : MaPrimeRénov', éco-prêt à taux zéro, TVA à taux réduit (5,5% ou 10% selon les travaux), aides des collectivités locales, certificats d'économie d'énergie, etc. Nous vous informons sur les aides auxquelles vous êtes éligible et pouvons vous accompagner dans certaines démarches administratives pour les obtenir.",
      category: "financement"
    },
    {
      question: "Combien de temps dure un chantier de réfection de toiture ?",
      answer: "La durée d'un chantier de réfection de toiture dépend de plusieurs facteurs : surface, complexité, conditions météorologiques et travaux annexes nécessaires. Pour une maison individuelle standard, comptez entre 1 et 3 semaines. Pour des bâtiments plus importants ou des configurations complexes, la durée peut s'étendre jusqu'à plusieurs mois. Nous établissons un planning précis avant le début des travaux et vous informons de tout ajustement nécessaire.",
      category: "chantier"
    },
    {
      question: "Comment protégez-vous mon habitation pendant les travaux de toiture ?",
      answer: "Nous mettons en place plusieurs mesures de protection : bâchage en cas d'intempéries, protection des sols et des meubles à l'intérieur si nécessaire, sécurisation des abords du chantier, et nettoyage quotidien des zones de travail. Notre équipe est formée pour minimiser les nuisances et préserver votre propriété. En fin de chantier, nous effectuons un nettoyage complet et un contrôle magnétique pour éliminer tout débris métallique (clous, vis) autour de votre maison.",
      category: "chantier"
    },
    {
      question: "Quels sont les signes indiquant qu'une toiture doit être rénovée ?",
      answer: "Plusieurs signes indiquent qu'une rénovation est nécessaire : tuiles cassées ou déplacées, traces d'humidité ou moisissures dans les combles, infiltrations visibles au plafond, affaissement de la toiture, mousse excessive, gouttières endommagées ou obstruées, et âge avancé des matériaux. Nous proposons un diagnostic gratuit pour évaluer l'état de votre toiture et vous recommander les interventions appropriées, de la simple réparation à la réfection complète.",
      category: "diagnostic"
    }
  ];

  // Get unique categories
  const categories = ['all', ...Array.from(new Set(faqs.map(faq => faq.category)))];

  // Filter FAQs based on search term
  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="pt-16 bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-primary-dark py-16 md:py-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary-light opacity-90"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Questions Fréquentes
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Trouvez des réponses aux questions les plus courantes sur nos services et nos méthodes de travail.
            </p>
          </motion.div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {/* Search Bar */}
        <div className="relative mb-10">
          <input
            type="text"
            placeholder="Rechercher une question..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 pl-10 pr-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-dark focus:border-transparent"
          />
          <FontAwesomeIcon 
            icon={faSearch} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>

        {/* Category Tabs */}
        <div className="mb-10">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === category
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Accordion */}
        <div>
          {filteredFaqs
            .filter(faq => activeCategory === 'all' || faq.category === activeCategory)
            .map((faq, index) => (
              <FaqAccordion 
                key={index} 
                faq={faq} 
              />
            ))}
          
          {filteredFaqs.filter(faq => activeCategory === 'all' || faq.category === activeCategory).length === 0 && (
            <div className="text-center py-10">
              <p className="text-gray-600 mb-4">Aucune question ne correspond à votre recherche.</p>
              <button 
                onClick={() => {setSearchTerm(''); setActiveCategory('all');}}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                Réinitialiser la recherche
              </button>
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Vous avez d'autres questions ?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Notre équipe est à votre disposition pour répondre à toutes vos interrogations.
          </p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-md transition-colors"
          >
            Contactez-nous
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Faq;
